import { useEffect, type ReactNode } from "react";
import { useForm, Controller, useWatch, useFieldArray, type Control } from "react-hook-form";
import { differenceInYears, parseISO } from "date-fns";
import { Modal } from "@/components/common/Modal";
import type { Student } from "@/types/assessment";

export interface GuardianFormValue {
  name: string;
  relationship: string;
  address: string;
  occupation: string;
  phone: string;
  email: string;
}

export interface StudentFormValues {
  name: string;
  gender: "male" | "female" | "other" | "unknown";
  birthdate: string;
  school: string;
  classroom: string;
  group: string;
  enrollmentDate: string;
  disabilityCategory: string;
  disabilityCause: string;
  disabilityLevel: string;
  guardians: GuardianFormValue[];
  notes: string;
}

interface StudentFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: StudentFormValues, options: { studentId?: string }) => Promise<void> | void;
  loading?: boolean;
  student?: Student;
  error?: string | null;
}

const createEmptyGuardian = (): GuardianFormValue => ({
  name: "",
  relationship: "",
  address: "",
  occupation: "",
  phone: "",
  email: ""
});

const DEFAULT_VALUES: StudentFormValues = {
  name: "",
  gender: "unknown",
  birthdate: "",
  school: "",
  classroom: "",
  group: "",
  enrollmentDate: "",
  disabilityCategory: "",
  disabilityCause: "",
  disabilityLevel: "",
  guardians: [createEmptyGuardian(), createEmptyGuardian()],
  notes: ""
};

export function StudentFormDialog({
  open,
  onClose,
  onSubmit,
  loading = false,
  student,
  error = null
}: StudentFormDialogProps): JSX.Element | null {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<StudentFormValues>({
    defaultValues: DEFAULT_VALUES
  });

  const { fields: guardianFields, append: appendGuardian, remove: removeGuardian } = useFieldArray({
    control,
    name: "guardians"
  });

  useEffect(() => {
    if (student && open) {
      reset({
        name: student.profile.name,
        gender: student.profile.gender,
        birthdate: student.profile.birthdate,
        school: student.enrollment.school,
        classroom: student.enrollment.classroom,
        group: student.enrollment.group ?? "",
        enrollmentDate: toMonthInputValue(student.enrollment.startDate),
        disabilityCategory: getMetadataString(student.metadata, "disabilityCategory"),
        disabilityCause: getMetadataString(student.metadata, "disabilityCause"),
        disabilityLevel: getMetadataString(student.metadata, "disabilityLevel"),
        guardians:
          student.guardians.length > 0
            ? student.guardians.map((guardian) => ({
                name: guardian.name,
                relationship: guardian.relationship,
                address: guardian.address ?? "",
                occupation: guardian.occupation ?? "",
                phone: guardian.phone ?? "",
                email: guardian.email ?? ""
              }))
            : [createEmptyGuardian()],
        notes: getMetadataString(student.metadata, "notes")
      });
    } else if (open) {
      reset({
        ...DEFAULT_VALUES,
        guardians: [createEmptyGuardian(), createEmptyGuardian()]
      });
    }
  }, [open, reset, student]);

  async function submit(values: StudentFormValues) {
    await onSubmit(values, { studentId: student?.id });
  }

  return (
    <Modal
      title={student ? "编辑儿童基本信息" : "新增儿童基本信息"}
      open={open}
      onClose={onClose}
      widthClassName="max-w-5xl"
    >
      <form className="space-y-6" onSubmit={handleSubmit(submit)}>
        <section className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">儿童基本信息</h3>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Controller
              control={control}
              name="name"
              rules={{ required: "请填写姓名" }}
              render={({ field }) => (
                <GridField label="儿童姓名" error={errors.name?.message}>
                  <input
                    {...field}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    placeholder="请输入儿童姓名"
                  />
                </GridField>
              )}
            />
            <Controller
              control={control}
              name="school"
              render={({ field }) => (
                <GridField label="学校">
                  <input
                    {...field}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    placeholder="星辰幼儿园"
                  />
                </GridField>
              )}
            />
            <Controller
              control={control}
              name="classroom"
              render={({ field }) => (
                <GridField label="班级">
                  <input
                    {...field}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    placeholder="大班（星光一班）"
                  />
                </GridField>
              )}
            />
            <Controller
              control={control}
              name="group"
              render={({ field }) => (
                <GridField label="小班">
                  <input
                    {...field}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    placeholder="如：晨星组"
                  />
                </GridField>
              )}
            />
            <Controller
              control={control}
              name="gender"
              render={({ field }) => (
                <GridField label="性别">
                  <select
                    {...field}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                  >
                    <option value="unknown">未指定</option>
                    <option value="male">男</option>
                    <option value="female">女</option>
                    <option value="other">其他</option>
                  </select>
                </GridField>
              )}
            />
            <Controller
              control={control}
              name="birthdate"
              rules={{ required: "请选择出生日期" }}
              render={({ field }) => (
                <GridField label="出生日期" error={errors.birthdate?.message}>
                  <input
                    {...field}
                    type="date"
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </GridField>
              )}
            />
            <AgePreview control={control} />
            <Controller
              control={control}
              name="enrollmentDate"
              render={({ field }) => (
                <GridField label="入学时间">
                  <input
                    {...field}
                    type="month"
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </GridField>
              )}
            />
            <Controller
              control={control}
              name="disabilityCategory"
              render={({ field }) => (
                <GridField label="障碍类别">
                  <input
                    {...field}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    placeholder="如：发展迟缓"
                  />
                </GridField>
              )}
            />
            <Controller
              control={control}
              name="disabilityCause"
              render={({ field }) => (
                <GridField label="障碍原因">
                  <input
                    {...field}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    placeholder="家长/医生反馈"
                  />
                </GridField>
              )}
            />
            <Controller
              control={control}
              name="disabilityLevel"
              render={({ field }) => (
                <GridField label="障碍程度">
                  <input
                    {...field}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    placeholder="轻度/中度/重度"
                  />
                </GridField>
              )}
            />
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-800">家长 / 监护人信息</h3>
            </div>
            <button
              type="button"
              onClick={() => appendGuardian(createEmptyGuardian())}
              className="inline-flex items-center rounded-md border border-brand px-3 py-1.5 text-sm font-semibold text-brand hover:bg-brand/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              新增监护人
            </button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">姓名</th>
                  <th className="px-3 py-2 text-left font-medium">称谓</th>
                  <th className="px-3 py-2 text-left font-medium">住址</th>
                  <th className="px-3 py-2 text-left font-medium">职业</th>
                  <th className="px-3 py-2 text-left font-medium">电话</th>
                  <th className="px-3 py-2 text-left font-medium">邮箱</th>
                  <th className="px-3 py-2 text-right font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {guardianFields.map((field, index) => (
                  <tr key={field.id} className="bg-white">
                    <td className="px-3 py-2">
                      <input
                        {...register(`guardians.${index}.name` as const)}
                        className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                        placeholder="姓名"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        {...register(`guardians.${index}.relationship` as const)}
                        className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                        placeholder="父亲/母亲"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        {...register(`guardians.${index}.address` as const)}
                        className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                        placeholder="XX 市 XX 区"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        {...register(`guardians.${index}.occupation` as const)}
                        className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                        placeholder="职业"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        {...register(`guardians.${index}.phone` as const)}
                        className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                        placeholder="13800000000"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        {...register(`guardians.${index}.email` as const)}
                        className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                        placeholder="email@example.com"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => removeGuardian(index)}
                        className="text-xs text-rose-600 hover:underline"
                      >
                        删除
                      </button>
                    </td>
                  </tr>
                ))}
                {guardianFields.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-500">
                      暂无监护人信息，请点击「新增监护人」。
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <Controller
          control={control}
          name="notes"
          render={({ field }) => (
            <Field label="备注">
              <textarea
                {...field}
                rows={3}
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                placeholder="可记录学习重点、兴趣特长等"
              />
            </Field>
          )}
        />

        <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-end">
          {error ? (
            <p className="text-sm font-medium text-red-600 sm:mr-auto" role="alert">
              {error}
            </p>
          ) : null}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white shadow hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {loading ? "保存中…" : "保存"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function Field({
  label,
  error,
  children
}: {
  label: string;
  error?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-sm text-slate-600">
      <span className="font-medium text-slate-700">{label}</span>
      {children}
      {error ? <span className="text-xs text-red-500">{error}</span> : null}
    </label>
  );
}

function GridField({
  label,
  error,
  children
}: {
  label: string;
  error?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-sm text-slate-600">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
      {error ? <span className="text-xs text-red-500">{error}</span> : null}
    </label>
  );
}

function AgePreview({ control }: { control: Control<StudentFormValues> }): JSX.Element {
  const birthdate = useWatch({ control, name: "birthdate" });
  let ageText = "-";
  if (birthdate) {
    const date = parseISO(birthdate);
    if (!Number.isNaN(date.getTime())) {
      ageText = `${differenceInYears(new Date(), date)} 岁`;
    }
  }

  return (
    <GridField label="年龄">
      <input
        value={ageText}
        readOnly
        className="w-full cursor-not-allowed rounded border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-500"
      />
    </GridField>
  );
}

function getMetadataString(metadata: Student["metadata"], key: string): string {
  if (!metadata) {
    return "";
  }
  const value = (metadata as Record<string, unknown>)[key];
  return typeof value === "string" ? (value as string) : "";
}

function toMonthInputValue(value: string | undefined): string {
  if (!value) {
    return "";
  }
  return /^\d{4}-\d{2}/.test(value) ? value.slice(0, 7) : "";
}
